import React, { Suspense } from 'react'
import { AuthRoute, NotFound, Route, Switch, Redirect } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { Alert } from '@fs/zion-ui'
import { useEffectiveUser } from '@fs/zion-user'
import CenteredLoadingSpinner from './components/shared/CenteredLoadingSpinner'
import UnauthorizedPage from './components/UnauthorizedPage'
import useToolsPermission from './hooks/useToolsPermission'

const HomePage = React.lazy(() => import('./components/HomePage'))
const WebToIndexPage = React.lazy(() => import('./components/WebToIndexPage'))
const AiAssistantChatLandingPage = React.lazy(() => import('./components/AiAssistantChatLandingPage'))
const AgentChatDemoPage = React.lazy(() => import('./components/agent-demo/AgentChatDemoPage'))
const DashboardPage = React.lazy(() => import('./components/DashboardPage'))

// Tools routes, gated by the portal's tools permission. Path and component are paired so the
// AuthRoute path list and the <Route>s can't drift apart.
const TOOLS_ROUTES = [
  { path: '/', component: HomePage },
  { path: '/web-to-index', component: WebToIndexPage },
  { path: '/agent-chat-demo', component: AgentChatDemoPage },
  { path: '/dashboard', component: DashboardPage },
]

function App() {
  const user = useEffectiveUser()

  return (
    <ErrorBoundary>
      <Suspense fallback={<CenteredLoadingSpinner />}>
        <Switch>
          {/* Labs-facing page: gated on sign-in inside the page, not on the tools permission */}
          <Route
            requirePersonalData
            exact
            path="/aiassistant/chat"
            render={() => <AiAssistantChatLandingPage user={user} />}
          />
          <Redirect exact from="/aiassistant" to="/aiassistant/chat" />

          <AuthRoute exact path={TOOLS_ROUTES.map((route) => route.path)}>
            <ToolsApp />
          </AuthRoute>

          {/* blank page for performance testing purposes only */}
          <Route path="/blank" component={() => 'This page is intentionally left blank'} />

          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  )
}

function ToolsApp() {
  const { hasToolsPermission, isLoading, isError, error } = useToolsPermission()

  if (isLoading) return <CenteredLoadingSpinner />
  if (isError) return <Alert type="error">{JSON.stringify(error)}</Alert>
  if (!hasToolsPermission) return <UnauthorizedPage />

  return (
    <Switch>
      {TOOLS_ROUTES.map(({ path, component }) => (
        <Route key={path} exact path={path} component={component} />
      ))}
    </Switch>
  )
}

export default App
